import type { AdAdapter, AdEvent, AdapterName } from "./types";

/**
 * The one rule for when an ad measurement may run, for every ad platform:
 * only while product analytics runs and only where the place allows it
 * (outside the European Economic Area, the United Kingdom and
 * Switzerland, and never with Global Privacy Control). A no to either is
 * a no to all of them. Until then no platform's script loads; once one
 * has, going off revokes it.
 *
 * With no adapter (a local build, the sample, the tests) nothing loads.
 */

/** How long an event waits for the consent to be read before it is dropped. */
export const PURCHASE_WAIT_MS = 30_000;

/** Product analytics as the relay follows it. */
export interface Analytics {
    active(): boolean;
    /** Calls the listener whenever analytics may have changed. Returns how to stop. */
    watch(listener: () => void): () => void;
}

export interface Relay {
    /**
     * Remembers the adapters, and follows analytics from now on. Loads nothing.
     * Called once, before `place`. A second call does not load a platform already loaded; `reset` does.
     */
    configure(adapters: readonly AdAdapter[]): void;
    /** Whether the place allows ad measurement. */
    place(allowed: boolean): void;
    /** Whether the adapters are counting now. */
    active(): boolean;
    /** Whether any adapter is configured. */
    configured(): boolean;
    /**
     * Counts an event, now when measurement runs, or once it starts within
     * {@link PURCHASE_WAIT_MS}, since the consent is read after the page opens.
     */
    track(event: AdEvent): void;
    /** For the tests: as the page loads, with nothing started. */
    reset(): void;
}

export function createRelay(analytics: Analytics): Relay {
    let adapters: readonly AdAdapter[] = [];
    let placed = false;
    let granted = false;
    let unwatch: (() => void) | null = null;
    let waiting: { event: AdEvent; at: number }[] = [];
    const loaded = new Set<AdapterName>();

    /**
     * Calls one adapter and keeps going if it throws. Only the adapter's name
     * is logged: the event carries an id, which is not for the console.
     */
    function safely<T>(adapter: AdAdapter, call: () => T, fallback: T): T {
        try {
            return call();
        } catch {
            console.warn("ad adapter failed", adapter.name);
            return fallback;
        }
    }

    function deliver(event: AdEvent): void {
        for (const adapter of adapters) {
            if (safely(adapter, () => adapter.accepts(event), false)) {
                safely(adapter, () => adapter.send(event), undefined);
            }
        }
    }

    function sync(): void {
        const on = adapters.length > 0 && placed && analytics.active();
        if (on === granted) return;
        granted = on;
        if (on) {
            for (const adapter of adapters) {
                if (!loaded.has(adapter.name)) {
                    loaded.add(adapter.name);
                    safely(adapter, () => adapter.load(), undefined);
                }
                safely(adapter, () => adapter.grant(), undefined);
            }
            const fresh = waiting.filter(({ at }) => Date.now() - at <= PURCHASE_WAIT_MS);
            waiting = [];
            for (const { event } of fresh) deliver(event);
        } else {
            for (const adapter of adapters) safely(adapter, () => adapter.revoke(), undefined);
        }
    }

    return {
        configure(next) {
            adapters = next;
            unwatch?.();
            unwatch = next.length > 0 ? analytics.watch(sync) : null;
        },
        place(allowed) {
            placed = allowed;
            if (!allowed) waiting = [];
            sync();
        },
        active: () => granted,
        configured: () => adapters.length > 0,
        track(event) {
            if (adapters.length === 0) return;
            if (granted) deliver(event);
            else waiting.push({ event, at: Date.now() });
        },
        reset() {
            unwatch?.();
            unwatch = null;
            adapters = [];
            placed = false;
            granted = false;
            waiting = [];
            loaded.clear();
        },
    };
}
